import styled from "styled-components";
import { useRef, useState } from "react";
import { Container, Divider, Row, SizedBox } from "../CommonBaseComponents";
import { C4Text } from "../text/CustomText";
import CustomIcon from "../image/CustomIcon";
import { useClickOutside } from "../../hooks/useClickOutside";
import Translate from "../../translate/Translate";
import SingleCheckedDropdownMenu from "../dropdown/SingleCheckedDropdownMenu";
import { Theme } from "../../Theme";

const MenuContainer = styled.div`
    position: relative;
    display: flex;
    align-items: center;
`;

const SearchBarTextInput = styled.input`
    width: ${(props) => props.width || "140px"};
    height: 20px;
    border: none;
    outline: none;
    padding: 0 10px;
    font-size: 14px;
    font-weight: 400;
    line-height: 20px;
    color: ${Theme.color.black};
    background-color: transparent;
`;

const StyledContainer = styled(Container)`
    &:focus-within {
        background-color: ${(props) => props.hoverBackgroundColor || props.backgroundColor};
    }
`;

// 선택된 라벨 길이에 따라 셀렉터 너비가 바뀌지 않도록,
// 가장 긴 라벨을 숨겨서 너비를 고정하고 실제 라벨을 그 위에 겹쳐 표시한다.
const LabelSizer = styled.div`
    position: relative;
    display: inline-flex;
    white-space: nowrap;
`;

const HiddenLabel = styled.div`
    visibility: hidden;
    pointer-events: none;
`;

const VisibleLabel = styled.div`
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    display: flex;
    align-items: center;
`;

function SearchBar({
    currentValue,
    setValue,
    totalWidth,
    selectorWidth,
    inputWidth,
    hasSearchSelector = true,
    type,
    values,
    isAdmin = false,
    searchValue,
    setSearchValue,
    onSearchButtonClick,
}) {
    let searchValues = values;
    if (type === "franchiseList") {
        // TODO: 다국어 처리
        searchValues = [
            { label: "Franchise Code", value: "franchiseCode" },
            { label: "Franchise Name", value: "franchiseName" },
        ];
    } else if (type === "marketing") {
        // TODO: 다국어 처리
        searchValues = [
            { label: "Coupon Code", value: "Coupon Code" },
            { label: "Coupon Name", value: "Coupon Name" },
        ];
    } else if (type === "coinTransactionList") {
        searchValues = [
            { label: Translate("revenue.transactionId"), value: "Transaction ID" },
            { label: Translate("store.shopCode"), value: "Shop Code" },
            { label: Translate("store.shopName"), value: "Shop Name" },
            { label: Translate("common.deviceName"), value: "Device Name" },
        ];
    } else if (type === "transactionList") {
        searchValues = [
            { label: Translate("revenue.transactionId"), value: "Transaction ID" },
            { label: Translate("store.shopCode"), value: "Shop Code" },
            { label: Translate("store.shopName"), value: "Shop Name" },
            { label: Translate("common.deviceName"), value: "Device Name" },
        ];
    } else if (type === "storeList") {
        searchValues = [
            // TODO: 다국어 처리
            ...(isAdmin
                ? [
                      { label: "Franchise Code", value: "Franchise Code" },
                      { label: "Franchise Name", value: "Franchise Name" },
                  ]
                : []),
            { label: Translate("store.shopCode"), value: "Shop Code" },
            { label: Translate("store.shopName"), value: "Shop Name" },
            { label: Translate("store.contact"), value: "Contact" },
            { label: Translate("store.ownerName"), value: "Owner Name" },
            { label: Translate("store.address"), value: "Address" },
        ];
    } else if (type === "ticketList") {
        searchValues = [
            { label: Translate("ticket.ticketNo"), value: "ticketNo" },
            { label: Translate("store.shopCode"), value: "shopCode" },
            { label: Translate("store.shopName"), value: "shopName" },
            { label: Translate("ticket.recipient"), value: "recipient" },
        ];
    } else if (type === "userList") {
        // TODO: 다국어 처리
        searchValues = [
            { label: "Phone Number", value: "phoneNumber" },
            { label: "Name", value: "name" },
            { label: "ID", value: "id" },
        ];
    } else if (type === "deviceStatus") {
        searchValues = [
            { label: Translate("store.deviceSearchType.all"), value: "all" },
            { label: Translate("store.deviceSearchType.device"), value: "device" },
            { label: Translate("store.deviceSearchType.errorCode"), value: "errorCode" },
        ];
    } else if (type === "faq") {
        searchValues = [
            { label: Translate("country.korea"), value: "korea" },
            { label: Translate("country.chile"), value: "chile" },
            { label: Translate("country.usa"), value: "usa" },
            { label: Translate("country.thailand"), value: "thailand" },
        ];
    }

    const selectedLabel = searchValues?.find((v) => v.value === currentValue)?.label;
    const longestLabel = (searchValues || []).reduce(
        (longest, v) => ((v.label?.length || 0) > longest.length ? v.label : longest),
        ""
    );

    const [isMenuOpen, setIsMenuOpen] = useState(false);

    const toggleMenu = () => {
        setIsMenuOpen((prev) => !prev);
    };

    const menuRef = useRef(null);
    useClickOutside(menuRef, () => {
        if (isMenuOpen) {
            setIsMenuOpen(false);
        }
    });

    return (
        <MenuContainer ref={menuRef}>
            <StyledContainer
                padding={"5px 10px"}
                backgroundColor={Theme.color.neutral100}
                borderRadius={"8px"}
                hoverBackgroundColor={Theme.color.zinc50}
                width={totalWidth}
            >
                <Row alignItem={"center"} width={"100%"}>
                    {hasSearchSelector && searchValues && (
                        <>
                            <Row alignItem={"flex-start"} cursor={"pointer"} onClick={toggleMenu} width={selectorWidth}>
                                <SizedBox width={"7px"} />
                                <LabelSizer>
                                    <HiddenLabel>
                                        <C4Text fontColor={Theme.color.zinc850}>{longestLabel}</C4Text>
                                    </HiddenLabel>
                                    <VisibleLabel>
                                        <C4Text fontColor={Theme.color.zinc850}>{selectedLabel}</C4Text>
                                    </VisibleLabel>
                                </LabelSizer>
                            </Row>
                            <SizedBox width={"4px"} cursor={"pointer"} onClick={toggleMenu} />
                            <CustomIcon
                                iconName={isMenuOpen ? "chevron_up" : "chevron_down"}
                                size={"16px"}
                                cursor={"pointer"}
                                onClick={toggleMenu}
                            />
                            <SizedBox width={"14px"} cursor={"pointer"} onClick={toggleMenu} />
                            <Divider orientation={"vertical"} color={Theme.color.zinc250} height={"20px"} />
                            <SizedBox width={"14px"} />
                        </>
                    )}
                    <SearchBarTextInput
                        placeholder="Search"
                        type="text"
                        width={inputWidth}
                        value={searchValue}
                        onFocus={() => {
                            if (isMenuOpen) setIsMenuOpen(false);
                        }}
                        onChange={(e) => setSearchValue?.(e.target.value)}
                        onKeyDown={(e) => {
                            if (e.key === "Enter") onSearchButtonClick();
                        }}
                    />
                    <SizedBox
                        width={"auto"}
                        height={"auto"}
                        cursor={"pointer"}
                        hoverBackgroundColor={Theme.color.neutral200}
                        padding={"7px"}
                        borderRadius={"6.67px"}
                        onClick={onSearchButtonClick}
                    >
                        <CustomIcon iconName={"search"} size={"13px"} cursor={"pointer"} />
                    </SizedBox>
                </Row>
            </StyledContainer>
            {isMenuOpen && searchValues && (
                <SingleCheckedDropdownMenu
                    initValue={currentValue}
                    setValue={setValue}
                    values={searchValues}
                    onClose={toggleMenu}
                    width={"max-content"}
                />
            )}
        </MenuContainer>
    );
}

export default SearchBar;
